import {
  FREQUENCY_OPTIONS,
  MandateStatus,
  TransactionOrderType,
  StepupFrequency,
} from "creso-core/constants";
import {
  isNewFolio,
  NEW_FOLIO_VALUE,
  isSIPOrder,
  formatAsCrore,
} from "creso-core/utils";
import {
  Form,
  FormField,
  FormSelect,
  V2Loader,
  FormItem,
  FormLabel,
  FormMessage,
  FormCheckbox,
  Button,
  FormDescription,
  cn,
  FormCurrencyInput,
  Badge,
} from "web-design-system";
import omit from "lodash/omit";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { maskValue } from "@core/utils/common";
import { useFieldArray, useForm } from "react-hook-form";
import {
  useMandates,
  useScheme,
  useUpdateSipTransaction,
  useProposal,
  useSchemes,
  useCreateSipTransactionV2,
  useInvalidateTransactionOrder,
  useLastUsedFolios,
} from "creso-core/hooks";
import { getFormattedAmount } from "@funds/utils";
import {
  NotepadText,
  ShoppingBasketIcon,
  TriangleAlertIcon,
  XIcon,
} from "lucide-react";
import {
  baseTransactionOrderSchema,
  sipFormValidationSchema,
} from "creso-core/zod";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { format, parse } from "date-fns";
import { APIError, isNonProduction } from "web-core/utils";
import { useRouter } from "@tanstack/react-router";
import { isEmpty } from "lodash";
import { useCheckStaleSip } from "@transactions/hooks/use-check-stale-sip";
import { FolioNumberDropdown, PlanStartDate } from "web-core";
import { TransactionContext } from "./transaction-wizard";
import FormActions from "./form-actions";
import { SchemesSearchDropdown } from "./schemes-search-dropdown";
import { handleSipFormValidationError } from "../utils/error";
import { BasketPicker } from "./import-collection";
import { useCollectionGroups } from "../hooks/use-collection-groups";
import { StepUpSipConfigComponent } from "./sip-step-up-config";

type DefaultValue = z.infer<typeof sipFormValidationSchema>;

const DEFAULT_VALUE: DefaultValue = {
  schemes: [
    {
      scheme: null,
      folio_id: null,
      amount: "",
      collection: null,
    },
  ],
  frequency: "MONTHLY" as const,
  mandate: null,
  first_order_today: true,
  start_date: "",
  order_id: null,
  step_up_amount: null,
  step_up_percentage: null,
  step_up_frequency: null,
};

const getStepUpPayload = (values: DefaultValue) => ({
  step_up_frequency: (values.step_up_frequency
    ? Number(values.step_up_frequency)
    : null) as StepupFrequency,
  step_up_amount: values.step_up_amount ? Number(values.step_up_amount) : null,
  step_up_percentage: values.step_up_percentage
    ? Number(values.step_up_percentage)
    : null,
});

type ActionType = z.infer<typeof baseTransactionOrderSchema.shape.action_type>;

const formTitle: Record<ActionType, string> = {
  CANCEL: "SIP Cancellation",
  PAUSE: "Pause SIP Order",
  RESUME: "Resume SIP Order",
  CREATE: "Update SIP Order",
  MODIFY: "Modify SIP Order",
};

export default function SIPForm() {
  const {
    selectedOrder,
    investment_account_guid = "",
    updateContext,
    collectionId,
    collectionType,
    collectionTarget,
  } = useContext(TransactionContext);

  const { data: scheme, isLoading: isSchemeLoading } = useScheme<"sip">(
    selectedOrder?.type === TransactionOrderType.XSIP
      ? selectedOrder?.scheme_id
      : 0,
    "sip"
  );

  const router = useRouter();

  const { mandates, isLoading: isMandatesLoading } = useMandates(
    investment_account_guid
  );

  const isCancelOrder = selectedOrder?.action_type === "CANCEL";
  const isPauseOrder = selectedOrder?.action_type === "PAUSE";
  const isResumeOrder = selectedOrder?.action_type === "RESUME";
  const isModifyOrder = selectedOrder?.action_type === "MODIFY";

  const disableForm = isCancelOrder || isPauseOrder || isResumeOrder;

  const { data: foliosWithLastUsedInfo } = useLastUsedFolios({
    investment_account_guid,
  });

  const { data: proposal, isLoading: isProposalLoading } = useProposal(
    { proposalId: collectionId as number },
    {
      enabled:
        !!collectionId &&
        collectionType === "proposal" &&
        collectionTarget?.includes(TransactionOrderType.XSIP),
    }
  );

  const proposalIsins = useMemo(
    () =>
      proposal?.proposal_funds
        .filter((f) => !f.source_folio)
        .filter((f) => f.type === "SIP")
        .map((f) => f.fund_scheme_isin),
    [proposal]
  );

  const { schemes: schemesFromProposals, isLoading: isLoadingSchemes } =
    useSchemes<"sip">({
      type: "sip",
      isin: proposalIsins?.join(","),
      enabled: !!proposalIsins?.length,
    });

  const formValues = useMemo(() => {
    // If there is a selected order from cart populate the form with its values
    // selectedOrder could also a scheme-folio-isin prefill data.
    // Prefer selectedOrder whenever it is defined in the context.
    if (isSIPOrder(selectedOrder)) {
      return {
        schemes: [
          {
            scheme: scheme ?? null,
            folio_id: selectedOrder.folio_id ?? null,
            amount: selectedOrder.amount,
          },
        ],
        frequency: selectedOrder.frequency ?? ("MONTHLY" as const),
        mandate:
          mandates?.find((m) => m.id === selectedOrder.mandate_id) ?? null,
        first_order_today: selectedOrder.first_order_today ?? true,
        start_date: selectedOrder.start_date,
        order_id: selectedOrder.id,
        step_up_amount: selectedOrder.step_up_amount ?? null,
        step_up_percentage: selectedOrder.step_up_percentage ?? null,
        step_up_frequency: selectedOrder.step_up_frequency ?? null,
      };
    }

    // If the collection target does not include XSIP, return the default values
    if (!collectionTarget?.includes(TransactionOrderType.XSIP))
      return DEFAULT_VALUE;

    // If there are schemes from proposals, populate the form with them
    if (schemesFromProposals) {
      return {
        ...DEFAULT_VALUE,
        schemes: schemesFromProposals?.map((s) => {
          const pFund = proposal?.proposal_funds.find(
            (f) => f.fund_scheme_isin === s.scheme_isin && f.type === "SIP"
          );
          const defaultFolio = foliosWithLastUsedInfo
            ?.filter((f) => f.internal_flag)
            .find((f) => f.amc_id === s.amc_id);
          return {
            scheme: s,
            amount: Number(pFund?.sip_amount ?? 0).toFixed(0),
            folio_id: defaultFolio?.id ?? pFund?.source_folio ?? null,
            collection: {
              name: proposal?.name ?? "",
              id: proposal?.id ?? 0,
              type: "proposal" as const,
            },
          };
        }),
      };
    }

    // If there are no schemes from proposals, return the default values
    return DEFAULT_VALUE;
  }, [
    schemesFromProposals,
    proposal,
    selectedOrder,
    mandates,
    scheme,
    collectionTarget,
    foliosWithLastUsedInfo,
  ]);

  const form = useForm({
    defaultValues: DEFAULT_VALUE,
    resolver: zodResolver(sipFormValidationSchema),
    disabled: disableForm,
  });

  useEffect(() => {
    form.reset(formValues);
  }, [form, formValues]);

  const { fields, append, remove, replace } = useFieldArray({
    control: form.control,
    name: "schemes",
  });

  const {
    collectionGroups,
    otherRows: otherSchemes,
    customCollectionKeys,
    handleRemoveCollection,
    handleRemoveScheme,
    handleAmountChange,
    recordOriginalAmounts,
    clearCustomKey,
    resetCollectionState,
  } = useCollectionGroups<DefaultValue["schemes"][number] & { id: string }>({
    rows: fields,
    remove,
    getCollection: (r) => r.collection,
    getSchemeKey: (r) => r.scheme?.scheme_isin ?? null,
    getAmount: (r) => r.amount,
  });

  const [editingCollectionId, setEditingCollectionId] = useState<number | null>(
    collectionType !== "proposal" ? (collectionId ?? null) : null
  );

  const createSipTransaction = useCreateSipTransactionV2();

  const invalidateDraftOrders = useInvalidateTransactionOrder(
    investment_account_guid
  );

  const updateSipTransaction = useUpdateSipTransaction(investment_account_guid);

  const handleUpdateOrder = useCallback(
    async (values: DefaultValue) => {
      const EMPTY_RESPONSE = { data: null, error: null, isHandled: true };
      const otherDetails = omit(values, [
        "schemes",
        "step_up_amount",
        "step_up_percentage",
        "step_up_frequency",
      ]);
      if (!values.order_id) return EMPTY_RESPONSE;
      if (!values.schemes[0].scheme?.scheme_isin) return EMPTY_RESPONSE;
      if (!otherDetails.mandate?.id) return EMPTY_RESPONSE;
      try {
        const result = await updateSipTransaction.mutateAsync({
          investment_account_guid,
          ...values.schemes[0],
          scheme_isin: values.schemes[0].scheme?.scheme_isin,
          folio_id: isNewFolio(values.schemes[0].folio_id)
            ? null
            : values.schemes[0].folio_id,
          ...otherDetails,
          ...getStepUpPayload(values),
          mandate_id: otherDetails.mandate?.id,
          id: values.order_id,
        });
        return { data: result, error: null };
      } catch (error) {
        return {
          data: null,
          error,
          isHandled: handleSipFormValidationError(error, form, 0),
        };
      }
    },
    [form, investment_account_guid, updateSipTransaction]
  );

  const handleCreateOrder = useCallback(
    async (values: DefaultValue) => {
      const otherDetails = omit(values, [
        "schemes",
        "step_up_amount",
        "step_up_percentage",
        "step_up_frequency",
      ]);
      const stepUpPayload = getStepUpPayload(values);
      const results = await Promise.allSettled(
        values.schemes.map((item) => {
          return createSipTransaction.mutateAsync({
            investment_account_guid,
            scheme_isin: item.scheme?.scheme_isin as string,
            folio_id: isNewFolio(item.folio_id) ? null : item.folio_id,
            amount: item.amount,
            ...otherDetails,
            ...stepUpPayload,
            mandate_id: otherDetails.mandate?.id as number,
            source_basket_id:
              item.collection?.type === "basket"
                ? (item.collection.id ?? null)
                : null,
          });
        })
      );

      await invalidateDraftOrders.invalidate();

      return results.map((r, idx) => {
        if (r.status === "fulfilled") {
          return { data: r.value, error: null };
        }
        return {
          data: null,
          error: r.reason,
          isHandled: handleSipFormValidationError(r.reason, form, idx),
        };
      });
    },
    [investment_account_guid, form, createSipTransaction, invalidateDraftOrders]
  );

  const onSubmit = useCallback(
    async (values: DefaultValue) => {
      try {
        if (values.order_id) {
          const result = await handleUpdateOrder(values);
          if (result.error) {
            if (result.isHandled === false) {
              toast.error("Failed to update order details", {
                description: (result.error as APIError)?.data?.message,
              });
            }
            return;
          }
        } else {
          const results = await handleCreateOrder(values);
          let hasErrors = false;
          const erroredSchemes: DefaultValue["schemes"] = [];
          results.forEach((r, idx) => {
            if (r.error) {
              hasErrors = true;
              if (r.isHandled === false) {
                toast.error("Failed to create order details", {
                  description: (r.error as APIError)?.data?.message,
                });
              }
              erroredSchemes.push(values.schemes[idx]);
            }
          });
          if (hasErrors) {
            form.setValue("schemes", erroredSchemes);
            return;
          }
        }
        resetCollectionState();
        form.reset(DEFAULT_VALUE);
        if (collectionType !== "proposal") {
          updateContext?.({
            selectedOrder: null,
            collectionId: undefined,
            collectionType: undefined,
          });
        } else {
          const newCollectionTarget = collectionTarget?.filter(
            (t) => t !== TransactionOrderType.XSIP
          );
          updateContext?.({
            selectedOrder: null,
            collectionId: isEmpty(newCollectionTarget)
              ? undefined
              : collectionId,
            collectionType: isEmpty(newCollectionTarget)
              ? undefined
              : collectionType,
            collectionTarget: newCollectionTarget,
          });
          if (newCollectionTarget?.length) {
            router.navigate({
              to: ".",
              search: (prev) => ({ ...prev, type: newCollectionTarget[0] }),
              replace: true,
            });
          }
        }
      } catch (error) {
        toast.error("Failed to save order details", {
          description: (error as Error).message,
        });
      }
    },
    [
      updateContext,
      form,
      handleCreateOrder,
      handleUpdateOrder,
      resetCollectionState,
      collectionTarget,
      collectionId,
      collectionType,
      router,
    ]
  );

  const mandateOptions = useMemo(() => {
    const addMandate = {
      label: "+ Add mandate",
      value: "add_mandate",
    };
    return (
      mandates
        ?.filter((d) => d.status !== MandateStatus.REJECTED)
        ?.map((d) => ({
          label: d.bank_account_details
            ? `${d.bank_account_details?.bank_name} ${maskValue(d.bank_account_details?.account_number, 4)} · ${d.status} · ${formatAsCrore(d.mandate_limit?.toString() ?? "0", 0)}`
            : d.id?.toString(),
          value: d.id?.toString(),
        })) || []
    ).concat(addMandate);
  }, [mandates]);

  const startDate = form.watch("start_date");

  const nextInstallmentDate = useMemo(() => {
    try {
      const startDateParsed = parse(startDate, "yyyy-MM-dd", new Date());
      return format(startDateParsed, "dd MMM yyyy");
    } catch {
      return "";
    }
  }, [startDate]);

  const { isStale, errors, fix } = useCheckStaleSip(
    isSIPOrder(selectedOrder) && isNonProduction() ? selectedOrder : undefined
  );

  const renderFundRow = (
    f: DefaultValue["schemes"][number] & { id: string },
    idx: number,
    collectionKey?: string
  ) => {
    return (
      <div key={f.id} className="grid grid-cols-7 w-full gap-2 items-start">
        <FormField
          className="col-span-3"
          control={form.control}
          name={`schemes.${idx}.scheme`}
          render={({ field }) => (
            <FormItem>
              <SchemesSearchDropdown
                value={
                  field.value
                    ? {
                        label: field.value.scheme_name,
                        value: field.value.scheme_isin,
                      }
                    : null
                }
                onChange={(val) => {
                  field.onChange(val);
                  const defaultFolio = foliosWithLastUsedInfo
                    ?.filter((f) => f.internal_flag)
                    .find((f) => f.amc_id === val?.amc_id);
                  if (defaultFolio) {
                    form.setValue(`schemes.${idx}.folio_id`, defaultFolio.id);
                  }
                }}
                placeholder="Select Scheme"
                type="sip"
                frequency={form.watch("frequency")}
                disabled={isModifyOrder || !!collectionKey}
              />
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          className="col-span-2"
          control={form.control}
          name={`schemes.${idx}.folio_id`}
          render={({ field }) => {
            return (
              <FormItem>
                <FolioNumberDropdown
                  key={form.watch(`schemes.${idx}.scheme`)?.amc_id}
                  amc_id={form.watch(`schemes.${idx}.scheme`)?.amc_id}
                  value={field.value?.toString() ?? NEW_FOLIO_VALUE}
                  onChange={(value) => {
                    if (value === NEW_FOLIO_VALUE) {
                      field.onChange(null);
                    } else {
                      field.onChange(value);
                    }
                  }}
                  filters={[(f) => f.internal_flag]}
                  investmentAccountGuid={investment_account_guid}
                  disabled={isModifyOrder}
                />
                <FormMessage />
              </FormItem>
            );
          }}
        />
        <div className="col-span-2 flex items-start gap-2">
          <FormField
            control={form.control}
            name={`schemes.${idx}.amount`}
            render={({ field }) => {
              const minAmount =
                form.watch(`schemes.${idx}.scheme`)
                  ?.minimum_installment_amount || "0";
              return (
                <FormCurrencyInput
                  description={`Min ${getFormattedAmount(minAmount)}`}
                  {...field}
                  placeholder="₹ Amount"
                  onChange={(e) => {
                    field.onChange(e);
                    if (collectionKey) {
                      handleAmountChange(collectionKey, e as string, idx);
                    }
                  }}
                />
              );
            }}
          />
          {fields.length > 1 && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="shrink-0"
              onClick={() => handleRemoveScheme(idx, collectionKey)}
            >
              <XIcon />
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col px-1 flex-1">
      <div className="flex flex-row justify-between items-center">
        <h5 className="mt-1">
          {selectedOrder?.id
            ? formTitle[selectedOrder?.action_type]
            : "Create SIP order"}
        </h5>
      </div>
      <div className="h-px bg-border w-full mt-3 mb-4" />
      <V2Loader
        className="relative"
        isLoading={
          isSchemeLoading ||
          isMandatesLoading ||
          isProposalLoading ||
          isLoadingSchemes
        }
      >
        {disableForm && (
          <div className="z-50 size-full bg-background/50 absolute top-0 left-0" />
        )}
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 flex-1 flex flex-col items-start"
          >
            <div
              className={cn(
                "flex flex-col w-full gap-6 p-4 rounded-lg bg-card"
              )}
            >
              <div className="grid grid-cols-10 w-full items-start gap-2">
                <FormField
                  control={form.control}
                  name="frequency"
                  className="col-span-3"
                  render={({ field }) => (
                    <FormSelect
                      label="Frequency"
                      options={FREQUENCY_OPTIONS}
                      value={field.value}
                      name={field.name}
                      onBlur={field.onBlur}
                      disabled={isModifyOrder}
                      onChange={(value) => {
                        if (isModifyOrder) return;
                        field.onChange(value);
                        form.setValue("start_date", "");
                        if (value === "DAILY") {
                          form.setValue("first_order_today", false);
                        }
                      }}
                    />
                  )}
                />
                <FormField
                  control={form.control}
                  className="col-span-4"
                  name="start_date"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>
                        {form.watch("frequency") === "WEEKLY"
                          ? "Start day"
                          : "Start date"}
                      </FormLabel>
                      <PlanStartDate
                        planType="XSIP"
                        frequency={form.watch("frequency")}
                        firstOrderToday={form.watch("first_order_today")}
                        placeholder={
                          form.watch("frequency") === "WEEKLY"
                            ? "Select start day"
                            : "Select start date"
                        }
                        onChange={field.onChange}
                        value={field.value}
                      />
                      {field.value && (
                        <FormDescription>
                          {form.watch("first_order_today") ? "Next" : "First"}{" "}
                          installment on {nextInstallmentDate}
                        </FormDescription>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  className="col-span-3"
                  control={form.control}
                  name="mandate"
                  render={({ field }) => (
                    <FormSelect
                      options={mandateOptions}
                      label="Choose Mandate"
                      align="end"
                      value={field.value?.id?.toString() ?? ""}
                      onChange={(value) => {
                        if (value === "add_mandate") {
                          router.navigate({
                            to: ".",
                            search: (prev: Record<string, unknown>) => ({
                              ...omit(prev, ["action", "type"]),
                              modal: "mandate" as const,
                              context: "create-form",
                            }),
                          });
                        } else {
                          field.onChange(
                            value
                              ? mandates?.find(
                                  (m) => m.id.toString() === value.toString()
                                )
                              : null
                          );
                        }
                      }}
                      name={field.name}
                      onBlur={field.onBlur}
                    />
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="first_order_today"
                render={({ field }) => (
                  <FormCheckbox
                    label="First order today"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={
                      isModifyOrder || form.watch("frequency") === "DAILY"
                    }
                  />
                )}
              />
              <div className="h-px w-full bg-border" />
              <StepUpSipConfigComponent
                disabled={isModifyOrder}
                value={{
                  amount: form.watch("step_up_amount") ?? null,
                  percentage: form.watch("step_up_percentage") ?? null,
                  frequency: form.watch("step_up_frequency") ?? null,
                }}
                onChange={(value) => {
                  form.setValue("step_up_amount", value.amount, {
                    shouldDirty: true,
                  });
                  form.setValue("step_up_percentage", value.percentage);
                  form.setValue("step_up_frequency", value.frequency);
                }}
                errors={{
                  amount: form.formState.errors.step_up_amount?.message,
                  percentage: form.formState.errors.step_up_percentage?.message,
                }}
              />
            </div>
            <div className="h-px w-full mb-4 bg-border"></div>
            {!selectedOrder?.id &&
            (!collectionGroups.length || editingCollectionId != null) ? (
              <BasketPicker<"sip">
                schemeType="sip"
                basketId={editingCollectionId}
                onCancel={() => {
                  setEditingCollectionId(null);
                  if (collectionType === "proposal") {
                    router.navigate({
                      to: ".",
                      search: (prev) =>
                        omit(prev as Record<string, unknown>, [
                          "collection_id",
                          "collection_type",
                        ]),
                      replace: true,
                    });
                  }
                }}
                onImport={async (results) => {
                  form.clearErrors();
                  const currentSchemes = form.getValues("schemes");
                  const importedId = results[0].collection.id;
                  const collectionKey = `collection_${importedId}`;

                  const internalLastUsedFolios = foliosWithLastUsedInfo?.filter(
                    (f) => f.internal_flag
                  );

                  type SchemeItem = DefaultValue["schemes"][number];
                  const filtered = currentSchemes.filter((s) => {
                    const col = (s as SchemeItem).collection;
                    if (!col) return true;
                    return `collection_${col.id ?? col.name}` !== collectionKey;
                  });

                  replace([
                    ...results.map((r) => {
                      const defaultFolio = internalLastUsedFolios?.find(
                        (f) => f.amc_id === r.scheme.amc_id
                      );
                      return {
                        scheme: r.scheme,
                        folio_id: defaultFolio?.id ?? null,
                        amount: r.amount,
                        collection: {
                          name: r.collection.name,
                          id: r.collection.id,
                          type: r.collection.type,
                        },
                      };
                    }),
                    ...filtered.filter(
                      (f) => f.scheme || f.folio_id || f.amount
                    ),
                  ]);

                  clearCustomKey(collectionKey);
                  recordOriginalAmounts(
                    importedId,
                    results.map((r) => ({
                      key: r.scheme.scheme_isin,
                      amount: r.amount,
                    }))
                  );
                  setEditingCollectionId(null);
                }}
              />
            ) : null}
            {collectionGroups
              .filter(({ collection }) => collection.id !== editingCollectionId)
              .map(({ key, schemes, collection }) => (
                <div
                  key={key}
                  className={cn(
                    "flex bg-linear-to-r from-tag-yellow to-background flex-col gap-4 border border-border rounded-lg px-4 py-3 w-full",
                    collection.type === "proposal" &&
                      "from-tag-green to-background"
                  )}
                >
                  <div className="flex items-center gap-2">
                    {collection.type === "basket" ? (
                      <ShoppingBasketIcon className="size-5 text-tag-yellow-foreground" />
                    ) : (
                      <NotepadText className="size-5 text-tag-green-foreground" />
                    )}
                    <span className="text-sm font-semibold">
                      {collection.name}
                    </span>
                    {customCollectionKeys.has(key) && (
                      <Badge
                        variant="custom"
                        className="text-tag-slate-foreground bg-tag-slate"
                      >
                        Custom
                      </Badge>
                    )}
                    <div className="ml-auto flex items-center gap-3">
                      {!customCollectionKeys.has(key) &&
                        collection.id != null &&
                        collection.type === "basket" && (
                          <Button
                            type="button"
                            variant="link"
                            className="text-xs h-auto p-0 font-medium hover:underline"
                            onClick={() =>
                              setEditingCollectionId(collection.id!)
                            }
                          >
                            Edit Total Amount
                          </Button>
                        )}
                      <Button
                        type="button"
                        variant="link"
                        className="text-xs h-auto p-0 font-medium text-destructive hover:underline"
                        onClick={() => {
                          handleRemoveCollection(key, schemes);
                          if (collection.type === "proposal") {
                            updateContext?.({
                              collectionTarget: collectionTarget?.filter(
                                (t) => t !== TransactionOrderType.XSIP
                              ),
                            });
                          }
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                  <div className="flex flex-col gap-4">
                    {schemes.map((f) => renderFundRow(f, f.idx, key))}
                  </div>
                </div>
              ))}
            <div
              className={cn(
                "space-y-4 w-full",
                collectionGroups.length && "px-4"
              )}
            >
              {otherSchemes.map((f) => renderFundRow(f, f.idx))}
            </div>
            {!selectedOrder?.id && (
              <Button
                onClick={() =>
                  append({
                    scheme: null,
                    folio_id: null,
                    amount: "",
                  })
                }
                type="button"
                variant="ghost"
                className={cn(
                  "-mt-4",
                  isCancelOrder && "hidden",
                  collectionGroups.length && "ml-2"
                )}
              >
                + Add another Scheme
              </Button>
            )}
            {selectedOrder?.id && isStale && isNonProduction() ? (
              <div className="border border-border bg-caution-foreground mt-auto rounded-lg px-3 py-2 w-full">
                <div className="flex flex-row items-start gap-2">
                  <TriangleAlertIcon className="size-4 stroke-caution mt-0.5" />
                  <div className="flex flex-col gap-1">
                    <span className="font-semibold text-sm">Stale Order</span>
                    <ol className="text-muted-foreground text-xs">
                      {errors.map((e) => (
                        <li key={e.type}>{e.message}</li>
                      ))}
                    </ol>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    type="button"
                    onClick={() => {
                      const fixedOrder = fix();
                      updateContext?.({
                        selectedOrder: fixedOrder,
                      });
                    }}
                    className="ml-auto self-center"
                  >
                    Fix
                  </Button>
                </div>
              </div>
            ) : null}
            <FormActions
              onReset={() => {
                resetCollectionState();
                form.reset();
              }}
              className={cn(
                selectedOrder?.id && isStale && isNonProduction()
                  ? "mt-0"
                  : "mt-auto"
              )}
              isUpdate={!!selectedOrder?.id}
              dirty={form.formState.isDirty}
            />
          </form>
        </Form>
      </V2Loader>
    </div>
  );
}
